/**
 * Dialog component for loading saved searches
 */

import DeleteIcon from "@mui/icons-material/Delete";
import {
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import React, { useEffect, useState } from "react";
import {
  SavedSearchData,
  listSavedSearches,
} from "../../services/savedSearchApi";

interface LoadSearchDialogProps {
  open: boolean;
  onClose: () => void;
  onLoad: (search: SavedSearchData) => void;
  onDelete: (id: string) => Promise<void>;
  entityType: "organizations" | "facilities" | "projects" | "sources";
}

const LoadSearchDialog: React.FC<LoadSearchDialogProps> = ({
  open,
  onClose,
  onLoad,
  onDelete,
  entityType,
}) => {
  const [savedSearches, setSavedSearches] = useState<SavedSearchData[]>([]);
  const [filteredSearches, setFilteredSearches] = useState<SavedSearchData[]>(
    []
  );
  const [searchTerm, setSearchTerm] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [deleteConfirmId, setDeleteConfirmId] = useState<string | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);

  // Load saved searches when dialog opens
  useEffect(() => {
    if (open) {
      loadSavedSearches();
    }
  }, [open, entityType]);

  // Filter searches based on search term
  useEffect(() => {
    if (searchTerm.trim()) {
      const term = searchTerm.toLowerCase();
      setFilteredSearches(
        savedSearches.filter(
          (search) =>
            search.name.toLowerCase().includes(term) ||
            search.description?.toLowerCase().includes(term)
        )
      );
    } else {
      setFilteredSearches(savedSearches);
    }
  }, [searchTerm, savedSearches]);

  const loadSavedSearches = async () => {
    setIsLoading(true);
    try {
      const searches = await listSavedSearches(entityType);
      setSavedSearches(searches);
      setFilteredSearches(searches);
    } catch (error) {
      return;
    } finally {
      setIsLoading(false);
    }
  };

  const handleLoad = (search: SavedSearchData) => {
    onLoad(search);
    onClose();
  };

  const handleDeleteClick = (id: string, event: React.MouseEvent) => {
    event.stopPropagation();
    setDeleteConfirmId(id);
  };

  const handleDeleteConfirm = async () => {
    if (!deleteConfirmId) return;

    setIsDeleting(true);
    try {
      await onDelete(deleteConfirmId);
      // Remove from local state
      setSavedSearches(savedSearches.filter((s) => s.id !== deleteConfirmId));
      setDeleteConfirmId(null);
    } catch (error) {
      return;
    } finally {
      setIsDeleting(false);
    }
  };

  const handleDeleteCancel = () => {
    setDeleteConfirmId(null);
  };

  const handleClose = () => {
    setSearchTerm("");
    setDeleteConfirmId(null);
    onClose();
  };

  const getFilterCount = (filters: Record<string, string>): number => {
    // Count non-empty filter values (excluding entityType)
    return Object.entries(filters).filter(
      ([key, value]) => key !== "entityType" && value && value.trim() !== ""
    ).length;
  };

  const formatDate = (dateString: string): string => {
    const date = new Date(dateString);
    return date.toLocaleDateString(undefined, {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  };

  const entityTypeLabels = {
    organizations: "Organizations",
    facilities: "Facilities",
    projects: "Projects",
    sources: "Sources",
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
      <DialogTitle>
        Load Saved Search
        <Typography variant="body2" color="text.secondary">
          {entityTypeLabels[entityType]} searches
        </Typography>
      </DialogTitle>
      <DialogContent>
        <TextField
          fullWidth
          placeholder="Search saved searches..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          sx={{ mb: 2 }}
          size="small"
        />

        {isLoading ? (
          <Box sx={{ textAlign: "center", py: 4 }}>
            <Typography color="text.secondary">Loading searches...</Typography>
          </Box>
        ) : filteredSearches.length === 0 ? (
          <Box sx={{ textAlign: "center", py: 4 }}>
            <Typography color="text.secondary">
              {savedSearches.length === 0
                ? `No saved searches for ${entityTypeLabels[entityType].toLowerCase()}`
                : "No searches match your filter"}
            </Typography>
          </Box>
        ) : (
          <List sx={{ maxHeight: 400, overflow: "auto" }}>
            {filteredSearches.map((search) => (
              <ListItem
                key={search.id}
                disablePadding
                secondaryAction={
                  deleteConfirmId === search.id ? (
                    <Box sx={{ display: "flex", gap: 1 }}>
                      <Button
                        size="small"
                        onClick={handleDeleteCancel}
                        disabled={isDeleting}
                      >
                        Cancel
                      </Button>
                      <Button
                        size="small"
                        color="error"
                        variant="contained"
                        onClick={handleDeleteConfirm}
                        disabled={isDeleting}
                      >
                        {isDeleting ? "Deleting..." : "Confirm"}
                      </Button>
                    </Box>
                  ) : (
                    <Tooltip title="Delete search">
                      <IconButton
                        edge="end"
                        onClick={(e) => handleDeleteClick(search.id!, e)}
                      >
                        <DeleteIcon />
                      </IconButton>
                    </Tooltip>
                  )
                }
              >
                <ListItemButton
                  onClick={() => handleLoad(search)}
                  disabled={deleteConfirmId === search.id}
                >
                  <ListItemText
                    primary={
                      <Box
                        sx={{ display: "flex", alignItems: "center", gap: 1 }}
                      >
                        <Typography component="span">{search.name}</Typography>
                        <Chip
                          label={entityTypeLabels[search.search_type]}
                          size="small"
                          variant="outlined"
                          sx={{ height: 20, fontSize: "0.7rem" }}
                        />
                      </Box>
                    }
                    secondary={
                      <>
                        {search.description && (
                          <Typography
                            component="span"
                            variant="body2"
                            color="text.secondary"
                            sx={{ display: "block" }}
                          >
                            {search.description}
                          </Typography>
                        )}
                        <Typography
                          component="span"
                          variant="caption"
                          color="text.secondary"
                        >
                          {getFilterCount(search.filters)} filters •{" "}
                          {search.updated_at && formatDate(search.updated_at)}
                          {search.created_by_name &&
                            ` • ${search.created_by_name}`}
                        </Typography>
                      </>
                    }
                  />
                </ListItemButton>
              </ListItem>
            ))}
          </List>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose}>Close</Button>
      </DialogActions>
    </Dialog>
  );
};

export default LoadSearchDialog;
